import { useEffect, useState } from "react";
import { useT, translateError } from "../../i18n/hooks.ts";
import {
  mappingCandidates,
  createSubBasinsManually,
  importSubBasinsFromShapefile,
  addFromMitecoBasin,
  emptyProject,
  editSubBasin,
  deleteSubBasin,
  addManualSubBasin,
  parseSubBasinsCsv,
  type SubBasinColumnMapping,
} from "../../model/projectEntities.ts";
import { subBasinCentroid, subBasinGeometry, type SubBasinLayer } from "../../model/gisLayer.ts";
import { subBasinsForBasin } from "../../model/mitecoLayers.ts";
import type { AemetQueuedRequest } from "../../model/aemet/aemetJobStore.ts";
import { ROCIO_COVERAGE_START, rocioDateRangeForSubBasinCount } from "../../model/rocio.ts";
import { daysBetweenIso } from "../../model/isoDate.ts";
import type { MitecoOrigin } from "../../model/storage.ts";
import type { EvalHidProject } from "../../model/types.ts";
import { loadGisFiles } from "../gisUpload.ts";
import { FileButton } from "../FileButton.tsx";
import { InfoIcon } from "../InfoIcon.tsx";
import { TrashIcon } from "../TrashIcon.tsx";
import { MitecoBasinPickerMap } from "./MitecoBasinPickerMap.tsx";

/** Result of Step1SubBasins.tsx's "MITECO" tab — a full wizard finish, not a normal step transition (see MitecoStep's own comment for why), with the extra bookkeeping App.tsx needs to record where the sub-basins came from and to launch their AEMET fetch once the project is actually open. */
export interface MitecoFinishPayload {
  project: EvalHidProject;
  layer: SubBasinLayer;
  mitecoOrigins: Record<number, MitecoOrigin>;
  aemetRequests: AemetQueuedRequest[];
}

/** Above this, the count preview warns (not blocks) that import + AEMET fetching will take a while — a large river basin can have hundreds of MITECO sub-basins. */
const MANY_SUB_BASINS_WARNING_THRESHOLD = 200;

/**
 * "MITECO" tab: pick one basin on the map, import every MITECO sub-basin
 * that falls inside it, create one HBV zone + one outlet point named after
 * the basin and assign both to all of them, then jump straight to
 * finishing the wizard (see `onFinishNow` — no further step makes sense
 * here, there's nothing left to choose). The AEMET fetch itself doesn't
 * start from here: `aemetJobStore` has no project/series to apply against
 * until the wizard actually finishes and the project is open (see
 * App.tsx's `aemetJobStore.setContext(null)` while `state.phase !==
 * "loaded"`) — this only builds the request list, App.tsx's `onComplete`
 * handler launches it.
 *
 * No button of its own: NewProject.tsx's own nav "Finalizar" (next to
 * "Atrás") does the finishing, via `onFinishReady` — a plain second
 * "Finalizar con estas subcuencas" button here would just be a confusing
 * duplicate. `onFinishReady(fn)` hands up the finish callback the moment a
 * basin has a non-empty result (`null` otherwise, including on unmount —
 * see the effect's own cleanup — so the nav button can't fire a stale
 * finish for a basin that's no longer selected).
 */
function MitecoStep({
  onFinishNow,
  onFinishReady,
}: {
  onFinishNow: (payload: MitecoFinishPayload) => void;
  onFinishReady: (finish: (() => void) | null) => void;
}) {
  const t = useT();
  const [selected, setSelected] = useState<GeoJSON.Feature | null>(null);
  const [foundSubBasins, setFoundSubBasins] = useState<GeoJSON.Feature[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function select(feature: GeoJSON.Feature) {
    setSelected(feature);
    setFoundSubBasins(null);
    setError(null);
    setSearching(true);
    subBasinsForBasin(feature)
      .then((found) => setFoundSubBasins(found))
      .catch((err) => setError(translateError(err, t)))
      .finally(() => setSearching(false));
  }

  function finish() {
    if (!selected || !foundSubBasins || foundSubBasins.length === 0) return;
    setError(null);
    try {
      const { project, layer, subBasinOrigins } = addFromMitecoBasin(
        emptyProject(),
        selected,
        foundSubBasins,
        t
      );
      const mitecoOrigins: Record<number, MitecoOrigin> = {};
      Object.entries(subBasinOrigins).forEach(([code, basinCode]) => {
        mitecoOrigins[Number(code)] = { layer: "subBasins", basinCode };
      });
      const rocioRange = rocioDateRangeForSubBasinCount(project.subBasins.length);
      const aemetRequests: AemetQueuedRequest[] = project.subBasins.flatMap((sb) => {
        const centroid = subBasinCentroid(layer, sb.subBasinCode);
        const geometry = subBasinGeometry(layer, sb.subBasinCode);
        return centroid
          ? [
              {
                subBasinCode: sb.subBasinCode,
                subBasinName: sb.description,
                centroid,
                opts: {
                  freeStart: rocioRange.start,
                  freeEnd: rocioRange.end,
                  method: "rocio",
                  geometry,
                },
              },
            ]
          : [];
      });
      onFinishNow({ project, layer, mitecoOrigins, aemetRequests });
    } catch (err) {
      setError(translateError(err, t));
    }
  }

  const ready = selected != null && foundSubBasins != null && foundSubBasins.length > 0;
  useEffect(() => {
    onFinishReady(ready ? finish : null);
    return () => onFinishReady(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const props = selected?.properties as { name: string; basinCode: string } | undefined;
  const basinLabel = props ? (props.name !== props.basinCode ? props.name : props.basinCode) : "";

  // Preview of the ROCIO range this many sub-basins would actually get —
  // see rocioDateRangeForSubBasinCount's own comment (rocio.ts) for why it
  // scales down for a large import. Only worth telling the user about when
  // it's actually LESS than the full record, not on every import.
  const rocioRange = foundSubBasins ? rocioDateRangeForSubBasinCount(foundSubBasins.length) : null;
  const rocioRangeReduced = rocioRange != null && rocioRange.start !== ROCIO_COVERAGE_START;
  const rocioYears = rocioRange
    ? Math.round((daysBetweenIso(rocioRange.start, rocioRange.end) + 1) / 365.25)
    : 0;

  return (
    <div>
      <p className="muted-text">{t("step1.mitecoHelp")}</p>
      <MitecoBasinPickerMap selected={selected} onSelect={select} />

      {selected && (
        <div className="miteco-result">
          {searching && (
            <p className="muted-text">{t("step1.mitecoSearchingIn", { name: basinLabel })}</p>
          )}
          {!searching && foundSubBasins && foundSubBasins.length === 0 && (
            <p className="error">{t("step1.mitecoNoneFoundIn", { name: basinLabel })}</p>
          )}
          {!searching && foundSubBasins && foundSubBasins.length > 0 && (
            <p>
              {t("step1.mitecoSummary", { name: basinLabel, n: foundSubBasins.length })}
              {foundSubBasins.length > MANY_SUB_BASINS_WARNING_THRESHOLD && (
                <InfoIcon text={t("step1.mitecoManyFound", { n: foundSubBasins.length })} />
              )}
              {rocioRangeReduced && (
                <InfoIcon text={t("step1.mitecoRocioRangeReduced", { years: rocioYears })} />
              )}
            </p>
          )}
          {error && <p className="error">{error}</p>}
        </div>
      )}
    </div>
  );
}

function ShapefileStep({
  onComplete,
}: {
  onComplete: (project: EvalHidProject, layer: SubBasinLayer | null) => void;
}) {
  const [geojson, setGeojson] = useState<GeoJSON.FeatureCollection | null>(null);
  const [mapping, setMapping] = useState<SubBasinColumnMapping>({
    nameField: "",
    areaField: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const t = useT();

  async function onFiles(files: FileList | null) {
    if (!files) return;
    setError(null);
    setNotice(null);
    try {
      const { geojson: fc, notice: loadedNotice } = await loadGisFiles(files, t);
      const { textFields, numericFields } = mappingCandidates(fc);
      setGeojson(fc);
      setNotice(loadedNotice);
      setMapping({
        nameField: textFields[0] ?? "",
        areaField: numericFields[0] ?? "",
      });
    } catch (err) {
      setError(translateError(err, t));
      setGeojson(null);
    }
  }

  const { textFields, numericFields } = geojson
    ? mappingCandidates(geojson)
    : { textFields: [], numericFields: [] };

  return (
    <div>
      <p>{t("step1.uploadShapefile")}</p>
      <FileButton multiple accept=".shp,.dbf,.prj,.shx,.cpg,.geojson,.json" onFiles={onFiles} />

      {error && <p className="error">{error}</p>}
      {notice && <p className="engine-notice">{notice}</p>}

      {geojson && (
        <div className="step-mapping">
          <p>{t("step1.geometriesFound", { n: geojson.features.length })}</p>
          <label>
            {t("gisLayerPanel.subBasinName")}
            <select
              value={mapping.nameField}
              onChange={(e) => setMapping({ ...mapping, nameField: e.target.value })}
            >
              {textFields.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t("resultsPanel.areaKm2")}
            <select
              value={mapping.areaField}
              onChange={(e) => setMapping({ ...mapping, areaField: e.target.value })}
            >
              {numericFields.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          <button
            className="button button-primary"
            disabled={!mapping.nameField || !mapping.areaField}
            onClick={() => {
              const { project, layer } = importSubBasinsFromShapefile(
                emptyProject(),
                geojson,
                mapping,
                t
              );
              onComplete(project, layer);
            }}
          >
            {t("step1.importButton", { n: geojson.features.length })}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Adds directly to `project` on every "Add" click (addManualSubBasin) —
 * doesn't stage entries locally behind its own "Create N sub-basins" button
 * like ShapefileStep/CsvStep do. A bulk import (shapefile, CSV) genuinely
 * needs one confirming action after the fact; typing rows one at a time
 * doesn't — "Add" already reads as "create this one now", so it does. The
 * moment the first one lands, `project.subBasins.length > 0` and
 * Step1SubBasins switches to rendering ExistingSubBasinsStep instead (its
 * own "Add another" row takes over from here).
 */
function ManualStep({
  project,
  onChangeProject,
}: {
  project: EvalHidProject;
  onChangeProject: (project: EvalHidProject) => void;
}) {
  const [name, setName] = useState("");
  const [area, setArea] = useState("0");
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  function add() {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    try {
      onChangeProject(addManualSubBasin(project, trimmedName, Number(area) || 0));
      setName("");
      setArea("0");
    } catch (err) {
      setError(translateError(err, t));
    }
  }

  return (
    <div>
      <div className="step-mapping">
        <label>
          {t("common.name")}
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
        </label>
        <label>
          {t("resultsPanel.areaKm2")}
          <input type="number" value={area} onChange={(e) => setArea(e.target.value)} />
        </label>
        <button className="button" onClick={add}>
          {t("common.add")}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function CsvStep({
  onComplete,
}: {
  onComplete: (project: EvalHidProject, layer: SubBasinLayer | null) => void;
}) {
  const [entries, setEntries] = useState<{ name: string; area: number }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const t = useT();

  async function onCsv(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    setError(null);
    setNotice(null);
    try {
      const parsed = parseSubBasinsCsv(await file.text());
      setEntries([...entries, ...parsed]);
      setNotice(t("step1.csvImported", { n: parsed.length }));
    } catch (err) {
      setError(translateError(err, t));
    }
  }

  return (
    <div>
      <p className="muted-text">{t("step1.csvHelp")}</p>
      <FileButton accept=".csv" onFiles={onCsv} label={t("step1.uploadCsvButton")} />
      {error && <p className="error">{error}</p>}
      {notice && <p className="engine-notice">{notice}</p>}

      {entries.length > 0 && (
        <>
          <ul className="zones-list">
            {entries.map((e, i) => (
              <li key={i}>{t("step1.inputSummary", { name: e.name, area: e.area })}</li>
            ))}
          </ul>
          <button
            className="button button-primary"
            onClick={() => onComplete(createSubBasinsManually(emptyProject(), entries), null)}
          >
            {t("step1.createButton", { n: entries.length })}
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Shown instead of ShapefileStep/ManualStep once sub-basins already exist —
 * reachable either the first time (right after creating them) or by going
 * "Atrás" from a later step (zones, outlets, series...) and back to step 1.
 *
 * Edits the SAME `project` in place (editSubBasin/deleteSubBasin/
 * addManualSubBasin — the exact functions the real editor uses later, not a
 * parallel "staging list" committed all at once) instead of restarting from
 * an empty list like the old ManualStep-only flow did. That old flow was a
 * real bug: going back to fix a typo in a sub-basin name showed an empty
 * form (nothing remembered), and creating sub-basins again from there called
 * `createSubBasinsManually(emptyProject(), ...)` — replacing the WHOLE
 * project with a fresh one, silently discarding any zone/outlet/topology
 * work already done in steps 2-4 for the sub-basins that already existed.
 * Editing in place instead keeps every existing `subBasinCode` (and
 * whatever's already assigned to it) untouched unless the user explicitly
 * deletes that row.
 */
function ExistingSubBasinsStep({
  project,
  onChangeProject,
}: {
  project: EvalHidProject;
  onChangeProject: (project: EvalHidProject) => void;
}) {
  const t = useT();
  const [newName, setNewName] = useState("");
  const [newArea, setNewArea] = useState("0");
  const [error, setError] = useState<string | null>(null);

  function rename(subBasinCode: number, description: string) {
    const subBasin = project.subBasins.find((c) => c.subBasinCode === subBasinCode);
    if (!subBasin || !description.trim()) return;
    onChangeProject(
      editSubBasin(project, subBasinCode, {
        description: description.trim(),
        area: subBasin.area,
        partialResults: subBasin.partialResults === 1,
        precipAdjustment: subBasin.precipAdjustment,
        etpAdjustment: subBasin.etpAdjustment,
        tempAdjustment: subBasin.tempAdjustment,
      })
    );
  }

  function changeArea(subBasinCode: number, area: string) {
    const subBasin = project.subBasins.find((c) => c.subBasinCode === subBasinCode);
    if (!subBasin) return;
    onChangeProject(
      editSubBasin(project, subBasinCode, {
        description: subBasin.description,
        area: Number(area) || 0,
        partialResults: subBasin.partialResults === 1,
        precipAdjustment: subBasin.precipAdjustment,
        etpAdjustment: subBasin.etpAdjustment,
        tempAdjustment: subBasin.tempAdjustment,
      })
    );
  }

  function addOne() {
    setError(null);
    try {
      onChangeProject(addManualSubBasin(project, newName, Number(newArea) || 0));
      setNewName("");
      setNewArea("0");
    } catch (err) {
      setError(translateError(err, t));
    }
  }

  return (
    <div>
      <h3>1. {t("panels.subBasins")}</h3>
      <p>{t("step1.subBasinsCreated", { n: project.subBasins.length })}</p>

      <div className="step-mapping">
        <label>
          {t("common.name")}
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addOne()}
          />
        </label>
        <label>
          {t("resultsPanel.areaKm2")}
          <input type="number" value={newArea} onChange={(e) => setNewArea(e.target.value)} />
        </label>
        <button className="button" onClick={addOne} disabled={!newName.trim()}>
          {t("step1.addAnother")}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
      <p className="muted-text">{t("step1.editHelp")}</p>

      <div className="assigner-controls sub-basins-editable-header">
        <span className="sub-basins-editable-header-label">{t("common.name")}</span>
        <span className="sub-basins-editable-header-label">{t("resultsPanel.areaKm2")}</span>
        <span className="sub-basins-editable-header-spacer" aria-hidden="true" />
      </div>
      <ul className="sub-basins-editable-list">
        {project.subBasins.map((c) => (
          <li key={c.subBasinCode} className="assigner-controls">
            <input
              defaultValue={c.description}
              onBlur={(e) => rename(c.subBasinCode, e.target.value)}
            />
            <input
              type="number"
              defaultValue={c.area}
              onBlur={(e) => changeArea(c.subBasinCode, e.target.value)}
            />
            <button
              className="button-icon button-danger"
              title={t("subBasinsPanel.deletePermanentTitle")}
              onClick={() => onChangeProject(deleteSubBasin(project, c.subBasinCode))}
            >
              <TrashIcon />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Step 1: create the sub-basins — uploading a shapefile (with column mapping, like InfoAgregarSubCuenc.vb), by hand, from a CSV, from MITECO, or if no GIS is available. */
export function Step1SubBasins({
  project,
  onChangeProject,
  onComplete,
  onFinishNow,
  onMitecoTabActive,
  onMitecoFinishReady,
}: {
  /** Already-created sub-basins, if any — see ExistingSubBasinsStep's own comment for why this matters (going back here used to silently lose them). */
  project: EvalHidProject | null;
  onChangeProject: (project: EvalHidProject) => void;
  onComplete: (project: EvalHidProject, layer: SubBasinLayer | null) => void;
  /** MitecoStep's own finish (see `onMitecoFinishReady`) — skips the rest of the wizard entirely, see MitecoFinishPayload's own comment for why. */
  onFinishNow: (payload: MitecoFinishPayload) => void;
  /**
   * Tells NewProject.tsx to hide its own generic "Siguiente" while the
   * MITECO tab is active: unlike every other tab here, MitecoStep never
   * calls `onChangeProject`/`onComplete` on its own — it only ever
   * produces a finished project via `onFinishNow` — so "Siguiente" would
   * silently move to step 2 with 0 sub-basins if left visible, no matter
   * how far along the basin search/preview is.
   */
  onMitecoTabActive: (active: boolean) => void;
  /** MitecoStep's own readiness (see its own comment) — feeds NewProject.tsx's regular nav "Finalizar" instead of a second, duplicate button inside the tab. */
  onMitecoFinishReady: (finish: (() => void) | null) => void;
}) {
  const [mode, setMode] = useState<"shapefile" | "manual" | "miteco" | "csv" | "vacio">(
    "shapefile"
  );
  const t = useT();

  useEffect(() => {
    onMitecoTabActive(mode === "miteco");
    return () => onMitecoTabActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  if (project && project.subBasins.length > 0) {
    return <ExistingSubBasinsStep project={project} onChangeProject={onChangeProject} />;
  }

  return (
    <div>
      <h3>1. {t("panels.subBasins")}</h3>
      <div className="edit-panel-tabs">
        <button
          className={mode === "shapefile" ? "tab-active" : undefined}
          onClick={() => setMode("shapefile")}
        >
          {t("step1.fromShapefile")}
        </button>
        <button
          className={mode === "manual" ? "tab-active" : undefined}
          onClick={() => setMode("manual")}
        >
          {t("step1.createManually")}
        </button>
        <button
          className={mode === "miteco" ? "tab-active" : undefined}
          onClick={() => setMode("miteco")}
        >
          {t("step1.mitecoTab")}
        </button>
        <button
          className={mode === "csv" ? "tab-active" : undefined}
          onClick={() => setMode("csv")}
        >
          {t("step1.csvTab")}
        </button>
        <button
          className={mode === "vacio" ? "tab-active" : undefined}
          onClick={() => setMode("vacio")}
        >
          {t("step1.noSubBasinsTab")}
        </button>
      </div>
      {mode === "shapefile" && <ShapefileStep onComplete={onComplete} />}
      {mode === "manual" && (
        <ManualStep project={project ?? emptyProject()} onChangeProject={onChangeProject} />
      )}
      {mode === "miteco" && (
        <MitecoStep onFinishNow={onFinishNow} onFinishReady={onMitecoFinishReady} />
      )}
      {mode === "csv" && <CsvStep onComplete={onComplete} />}
    </div>
  );
}
